<template>
  <!-- Only render on project level -->
  <div :class="$style.selectionContainer">
    <div class="block">
      <b-collapse
        :open.sync="isCollapseOpen"
        aria-id="contentIdForA11y2"
        animation="slide"
        v-model="isCollapseOpen"
      >
        <template #trigger>
          <div :class="$style.containerHeading">
            <div :class="$style.checkboxContainer">
              <!-- Checks all boxes -->
              <b-checkbox
                v-model="checkedAll"
                @click.native.stop=""
                :class="{
                  [$style.cursorNotAllowed]: !upcomingAuditIds.length,
                  [$style.cursorAllowed]: upcomingAuditIds.length,
                }"
                :disabled="!upcomingAuditIds.length"
              />
              <p>Add to Cycles</p>
            </div>
            <div :class="$style.chevron">
              <b-icon
                :icon="!isCollapseOpen ? 'chevron-down' : 'chevron-up'"
                size="is-small"
              />
            </div>
          </div>
        </template>
        <div :class="$style.noActiveCycles" v-if="!isActiveCycles">
          <div :class="$style.noActiveCycles">
            <p>No active cycles</p>
          </div>
        </div>

        <div
          v-if="isActiveCycles"
          :class="$style.panelBlock"
          v-for="audit in auditList"
          :key="audit.id"
        >
          <div :class="[$style.checkboxContainer]">
            <b-checkbox
              v-model="selectedAuditCycles"
              :native-value="audit.id"
              @click.native.stop=""
              :checked="audit.id"
              :disabled="isAuditCycleCommencedOrEnded(audit)"
              :class="{
                [$style.cursorNotAllowed]: isAuditCycleCommencedOrEnded(audit),
                [$style.cursorAllowed]: !isAuditCycleCommencedOrEnded(audit),
              }"
            />
            <p
              :class="{
                [$style.textDisabled]: isAuditCycleCommencedOrEnded(audit),
              }"
            >
              {{ audit.name }}
            </p>
            <p :class="$style.lightText">{{ audit.auditCategory }}</p>
            <b-tooltip
              v-if="isAuditCycleCommencedOrEnded(audit)"
              :label="tooltipInfoMsg(audit)"
              position="is-right"
              :class="$style.textDisabled"
            >
              <b-icon pack="mdi" icon="information-outline" size="is-small" />
            </b-tooltip>
          </div>
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';
import {
  AuditDetail,
  AuditStatus,
} from '@/store/modules/audits/types/audits.types';
import { ProjectDetailPayload } from '@/store/modules/projects/types/projects.types';
import { UserRoleInAudit } from '../../../store/modules/projects/types/projects.types';

@Component
export default class AddProjectCriteriaIntoAuditCycle extends Vue {
  @Prop() public selectedAuditCycleIds!: number[];

  public isCollapseOpen: boolean = false;
  public selectedAuditCycles: number[] = [];
  public checkedAll: boolean = false;

  @State((state) => state.projects.projectDetail)
  public projectDetail!: ProjectDetailPayload;

  get projectIdFromParam() {
    return +this.$route.params.projectId;
  }

  /** Filter audit list based on user role */
  get auditList(): AuditDetail[] {
    return this.projectDetail && this.projectDetail.audits
      ? this.projectDetail.audits.filter(
          (audit) =>
            audit.userRoleInAudit === UserRoleInAudit.isOwner ||
            audit.userRoleInAudit === UserRoleInAudit.isProjectCoOrdinator,
        )
      : [];
  }

  get upcomingAuditIds(): number[] {
    /** criteria can be added only in active upcoming audit cycles */
    return this.auditList
      .filter(
        (audit) =>
          audit.status === AuditStatus.ACTIVE &&
          audit.startedAt === null &&
          audit.endedAt === null,
      )
      .map((audit) => audit.id);
  }

  get isActiveCycles(): boolean {
    return this.auditList.some((audit) => audit.status === AuditStatus.ACTIVE);
  }

  mounted() {
    this.selectedAuditCycles = this.selectedAuditCycleIds;
  }

  isAuditCycleCommencedOrEnded(audit: AuditDetail): boolean {
    return (
      audit.status === AuditStatus.ARCHIVED ||
      audit.startedAt !== null ||
      audit.endedAt !== null
    );
  }

  tooltipInfoMsg(audit: AuditDetail): string {
    if (audit.startedAt !== null) {
      return 'This cycle has already started';
    } else if (audit.endedAt !== null) {
      return 'This cycle has already ended';
    } else if (audit.status === AuditStatus.ARCHIVED) {
      return 'This cycle has Archived';
    } else {
      return '';
    }
  }

  @Watch('selectedAuditCycles', { deep: true })
  onSelectedAuditCycles() {
    if (
      this.selectedAuditCycles.length &&
      this.selectedAuditCycles.length === this.upcomingAuditIds.length
    ) {
      this.checkedAll = true;
    } else {
      this.checkedAll = false;
    }

    this.$emit('selectedAuditCycles', this.selectedAuditCycles);
  }

  @Watch('checkedAll')
  onCheckAll() {
    if (this.checkedAll) {
      this.selectedAuditCycles = [...this.upcomingAuditIds];
    } else if (
      !this.checkedAll &&
      this.selectedAuditCycles.length === this.upcomingAuditIds.length
    ) {
      this.selectedAuditCycles = [];
    }

    this.$emit('selectedAuditCycles', this.selectedAuditCycles);
  }
}
</script>

<style lang="scss" module>
.selectionContainer {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin: 4px;
  background-color: #fff;
  color: #444f51;
  padding: 0.25rem 0;
}

.containerHeading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  font-size: 14px;

  .checkboxContainer {
    display: flex;
    column-gap: 8px;
  }
}

.chevron {
  p {
    font-size: 1rem;
    font-weight: 400;
  }

  i {
    padding-top: 50%;
    color: $grey-3;
    font-size: 20px;
  }
}

.panelBlock {
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
  gap: 1em;
  font-size: 14px;

  .checkboxContainer {
    display: flex;
    column-gap: 8px;
    width: 100%;
    border-bottom: 1px solid $grey-border;
    padding: 0.75rem 0;
    color: #444f51;

    .textDisabled,
    .lightText {
      color: #9f9f9f;
    }
  }
}

.panelBlock:last-child .checkboxContainer {
  border-bottom: none;
}

.checkboxContainer {
  padding-left: 2%;
  display: flex;
  align-items: center;
  gap: 1em;
}

.cursorAllowed {
  cursor: pointer;
}

.cursorNotAllowed {
  cursor: not-allowed;

  p,
  i {
    color: $grey-3;
  }
}

.noActiveCycles {
  padding: 0.5em 1em;
}
</style>
